import { useState } from 'react'
import { Bell, Check, MailOpen, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { motion } from 'framer-motion'
import { toast } from 'sonner'
import api from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

export function DashboardMessages() {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['notifications', page],
    queryFn: async () => {
      const response = await api.get('/api/v1/users/notifications/', { params: { page } })
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const markReadMutation = useMutation({
    mutationFn: (alias) => api.post(`/api/v1/users/notifications/${alias}/read/`),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['unreadNotificationCount'])
      queryClient.invalidateQueries(['recentNotifications'])
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => api.post('/api/v1/users/notifications/read-all/'),
    onSuccess: () => {
      queryClient.invalidateQueries(['notifications'])
      queryClient.invalidateQueries(['unreadNotificationCount'])
      queryClient.invalidateQueries(['recentNotifications'])
      toast.success('All messages marked as read')
    },
  })

  const notifications = data?.results || []
  const hasNext = Boolean(data?.next)
  const hasPrevious = page > 1

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleString()
  }

  return (
    <div className="w-full max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Bell className="w-8 h-8 text-primary" />
          Messages
        </h1>
        <p className="text-gray-600">Notifications and updates from the admin team</p>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-semibold text-gray-900">All Messages</h2>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {notifications.some(n => !n.is_read) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isLoading}
                className="flex items-center gap-2"
              >
                <MailOpen className="w-4 h-4" />
                Mark all as read
              </Button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="py-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-gray-500 mt-4">Loading messages...</p>
          </div>
        ) : error ? (
          <div className="py-12 text-center">
            <Bell className="w-12 h-12 text-red-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700 mb-1">Failed to load messages</p>
            <p className="text-xs text-gray-500">{error?.message || 'Please try again later'}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-700 mb-1">No messages yet</p>
            <p className="text-xs text-gray-500">When you receive notifications, they will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <motion.div
                key={notification.alias}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`p-4 rounded-lg border transition-colors ${
                  notification.is_read
                    ? 'bg-gray-50 border-gray-200 opacity-70'
                    : 'bg-white border-gray-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                    notification.is_read ? 'bg-gray-300' : 'bg-primary'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className={`text-sm font-semibold ${
                          notification.is_read ? 'text-gray-700' : 'text-gray-900'
                        }`}>
                          {notification.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(notification.created_at)}
                          {notification.sender_name && (
                            <span className="ml-2">from {notification.sender_name}</span>
                          )}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markReadMutation.mutate(notification.alias)}
                          disabled={markReadMutation.isLoading}
                          className="flex items-center gap-1 text-xs h-8"
                        >
                          <Check className="w-3 h-3" />
                          Mark read
                        </Button>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                      {notification.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {(hasPrevious || hasNext) && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={!hasPrevious || isLoading}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={!hasNext || isLoading}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
